import { Controller } from 'stimulus';

import { addClass, removeClass, hasClass } from '../../../dom-helper';

export default class extends Controller {
  static targets = ['button', 'alertContainer', 'alertMessage'];

  buttonTarget: any;
  alertContainerTarget: any;
  alertMessageTarget: any;

  itemId: string;
  statusInterval: any;
  alertTimeout: any;
  originalButtonHTML: string | null = null;

  connect() {
    // Get item ID from the URL
    const pathParts = window.location.pathname.split('/');
    this.itemId = pathParts[pathParts.length - 1];
    
    // Store original button HTML
    if (this.buttonTarget) {
      this.originalButtonHTML = this.buttonTarget.innerHTML;
    }
    
    // Check status immediately on page load
    // If there's a pending reindex, show alert and start polling
    //this.checkStatus();
  }

  disconnect() {
    if (this.statusInterval) {
      clearInterval(this.statusInterval);
    }
    if (this.alertTimeout) {
      clearTimeout(this.alertTimeout);
    }
  }

  async reSync(event: Event) {
    event.preventDefault();
    
    if (!this.buttonTarget) {
      return;
    }
    
    const button = this.buttonTarget;
    const originalText = button.innerHTML;
    
    // Disable button and show loading state
    //button.disabled = true;
    //button.innerHTML = '<span class="-color-black">Reindexing...</span>';
    //removeClass(button, '-color-asset');
    //addClass(button, '-color-gray');
    
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
      const response = await fetch(`/items/${this.itemId}/re_sync`, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': csrfToken,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        credentials: 'same-origin'
      });
      
      const data = await response.json();
      
      if (response.ok) {
        // Success - show pending message from controller response
        // Controller already set status to 'pending' before enqueueing job
        if (data && data.status === 'pending') {
          this.showAlert('Reindex started successfully. This may take a few moments...', '-color-green', false);
          // Start polling to check when it completes (only for completed/error, not pending)
          // this.startPolling();
        } else {
          // Fallback: start polling anyway
          // this.startPolling();
        }
      } else {
        // Handle validation/error responses
        let errorMessage = 'Failed to start reindex';
        let alertColor = '-color-red'; // Default to red for errors
        
        if (data && data.error) {
          errorMessage = data.error;
          
          // If it's a 422 with pending status, show as yellow (in progress)
          // This means reindex is already in progress from a previous request
          if (response.status === 422 && data.status === 'pending') {
            alertColor = '-color-green';
            this.showAlert('Reindex is already in progress. Please wait for it to complete.', alertColor, true);
            // Don't restore button - keep it disabled and start polling
            // this.startPolling();
            return; // Exit early, button stays disabled
          }
          
          // If it's a 422 with time restriction message, show as warning instead of error
          if (response.status === 422 && errorMessage.includes('Please wait')) {
            alertColor = '-color-green';
          }
        } else if (response.status === 403) {
          errorMessage = 'You are not authorized to reindex this item';
        } else if (response.status === 422) {
          errorMessage = 'Cannot reindex at this time';
          alertColor = '-color-green';
        }
        
        // Show alert with appropriate color
        this.showAlert(errorMessage, alertColor);
        
        // Restore button
        button.disabled = false;
        button.innerHTML = originalText;
        removeClass(button, '-color-gray');
        addClass(button, '-color-asset');
      }
    } catch (error) {
      // Network or parsing errors
      this.showAlert('Failed to start reindex. Please try again.', '-color-red');
      
      // Restore button
      button.disabled = false;
      button.innerHTML = originalText;
      removeClass(button, '-color-gray');
      addClass(button, '-color-asset');
    }
  }

  showAlert(message: string, colorClass: string = '-color-red', autoHide: boolean = true) {
    if (!this.alertContainerTarget || !this.alertMessageTarget) {
      return;
    }

    // Clear any existing timeout
    if (this.alertTimeout) {
      clearTimeout(this.alertTimeout);
    }

    // Show the alert
    removeClass(this.alertContainerTarget, '-state-hidden');
    
    // Set alert color (red for errors, yellow for warnings, green for success)
    this.alertContainerTarget.className = this.alertContainerTarget.className.replace(/-color-\w+/g, '');
    this.alertContainerTarget.classList.add(colorClass);
    
    // Update message
    this.alertMessageTarget.textContent = message;
    
    // Hide alert after 3 seconds if autoHide is true
    if (autoHide) {
      this.alertTimeout = setTimeout(() => {
        this.hideAlert();
      }, 5000);
    }
  }

  hideAlert() {
    if (this.alertContainerTarget) {
      addClass(this.alertContainerTarget, '-state-hidden');
    }
    // Clear timeout if alert is manually hidden
    if (this.alertTimeout) {
      clearTimeout(this.alertTimeout);
      this.alertTimeout = null;
    }
  }

  startPolling() {
    // Clear existing interval if any
    if (this.statusInterval) {
      clearInterval(this.statusInterval);
    }
    
    // Poll every 3 seconds to check for completion/error
    this.statusInterval = setInterval(() => {
      this.checkStatus();
    }, 3000);
  }

  async checkStatus() {
    try {
      const response = await fetch(`/items/${this.itemId}/re_sync_status`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        },
        credentials: 'same-origin'
      });
      
      if (response.ok) {
        const data = await response.json();
        
        if (data) {
          const status = data.status || 'none';
          
          // Only poll for completion/error, not pending
          // Pending status is shown from controller response
          if (status === 'pending') {
            // Keep showing pending message (don't auto-hide)
            this.showAlert('Reindex in progress. This may take a few moments...', '-color-green', true);
            
            // Start polling if not already polling (for page loads with existing pending reindex)
            if (!this.statusInterval) {
              this.startPolling();
            }
          } else if (status === 'completed') {
            // Show success message and auto-hide after 3 seconds
            const completedAt = data.completed_at;
            let message = 'Reindex completed successfully.';
            if (completedAt) {
              const date = new Date(completedAt);
              const formattedDate = date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
              const formattedTime = date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
              message = `Reindex completed successfully at ${formattedDate} ${formattedTime}.`;
            }
            this.showAlert(message, '-color-green');
            
            // Restore button
            this.restoreButton();
            
            // Stop polling
            this.stopPolling();
          } else if (status === 'error') {
            // Show error message and auto-hide after 3 seconds
            this.showAlert('Reindex failed. Please try again.', '-color-red');
            
            // Restore button
            this.restoreButton();
            
            // Stop polling
            this.stopPolling();
          } else {
            // No status - hide alert and stop polling
            this.hideAlert();
            this.stopPolling();
          }
        }
      }
    } catch (error) {
      // Silently fail - don't show errors for status checks
    }
  }

  stopPolling() {
    if (this.statusInterval) {
      clearInterval(this.statusInterval);
      this.statusInterval = null;
    }
  }

  restoreButton() {
    if (!this.buttonTarget) {
      return;
    }
    
    const button = this.buttonTarget;
    button.disabled = false;
    
    // Restore original HTML if we have it, otherwise use default
    if (this.originalButtonHTML) {
      button.innerHTML = this.originalButtonHTML;
    } else {
      button.innerHTML = '<span class="-color-black"><svg>...</svg>Re-sync</span>';
    }
    
    removeClass(button, '-color-gray');
    addClass(button, '-color-asset');
  }
}

